import { useEffect, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Check,
  GitMerge,
  History,
  Landmark,
  Loader2,
  PauseCircle,
  ShieldCheck,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { findBankByName, getBankLogoUrl } from '@/lib/banks';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';
import { useCurrency } from '@/contexts';
import type { Database } from '@/lib/database.types';
import type { TranslationKey } from '@/lib/i18n';

type Account = Database['public']['Tables']['accounts']['Row'];

/** What a bank can hold. Cash, a car or a watch has no bank account to sync with. */
const LINKABLE_SUBTYPES = new Set([
  'checking',
  'joint_account',
  'savings',
  'money_market',
  'investment',
  'pea_stock',
  'credit_card',
  'loan',
  'mortgage',
]);

export function isLinkableToBank(account: Pick<Account, 'source' | 'subtype'>): boolean {
  return account.source === 'manual' && LINKABLE_SUBTYPES.has(account.subtype);
}

/**
 * The accounts the bank just connected handed over — and only those.
 *
 * Never clevia's own account list: the user is being asked which *bank* account
 * this manual account is, and a list of things they already track cannot answer
 * that question.
 */
export function bankAccountsOfConnection(
  accounts: Account[],
  connectionId: string | null
): Account[] {
  if (!connectionId) return [];
  return accounts
    .filter((a) => a.source === 'powens' && a.powens_connection_id === connectionId)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Why a bank account cannot take this manual account over, or null when it can.
 *
 * `alreadyConnected` is the one that matters: reconnecting a bank clevia
 * already holds gives its accounts new Powens ids, so the fingerprint (a hash
 * of the IBAN, written by the sync) is what recognises them. Without it the
 * same account would sync twice, doubling it in net worth. The server checks
 * all three again — this is what keeps the user from finding out after the
 * fact.
 */
export function blockingReason(
  manual: Account,
  candidate: Account,
  accounts: Account[]
): 'alreadyConnected' | 'type' | 'currency' | null {
  const heldElsewhere =
    candidate.powens_fingerprint != null &&
    accounts.some(
      (other) =>
        other.id !== candidate.id &&
        other.id !== manual.id &&
        other.source === 'powens' &&
        other.powens_fingerprint === candidate.powens_fingerprint
    );

  if (candidate.powens_linked_at != null || heldElsewhere) return 'alreadyConnected';
  if (candidate.type !== manual.type) return 'type';
  if (manual.currency && candidate.currency && manual.currency !== candidate.currency) {
    return 'currency';
  }
  return null;
}

/**
 * The bank account this manual account used to be, if it is on offer.
 *
 * An account that lost synchronisation keeps the fingerprint of the bank
 * account it was fed by, so resubscribing puts the user back in front of the
 * same one — preselected and named — instead of leaving them to guess and risk
 * a second account for the same money.
 */
export function previouslySyncedCandidate(
  manual: Account,
  candidates: Account[],
  accounts: Account[]
): Account | undefined {
  if (!manual.powens_fingerprint) return undefined;
  return candidates.find(
    (candidate) =>
      candidate.powens_fingerprint === manual.powens_fingerprint &&
      blockingReason(manual, candidate, accounts) === null
  );
}

const BLOCKED_KEY: Record<NonNullable<ReturnType<typeof blockingReason>>, TranslationKey> = {
  alreadyConnected: 'accountLink.alreadyConnected',
  type: 'accountLink.blockedType',
  currency: 'accountLink.blockedCurrency',
};

export interface LinkSummary {
  dry_run: boolean;
  account_id: string;
  bank_account_name: string;
  institution: string | null;
  transactions_from: string;
  bank_transactions: number;
  merged_duplicates: number;
  unmatched_manual: number;
  unmatched_manual_sample: {
    id: string;
    date: string;
    amount: number;
    type: string;
    description: string | null;
  }[];
  recurring_paused: number;
  balance_before: number;
  balance_after: number;
}

const LINK_ERRORS = [
  'link_account_not_found',
  'link_not_manual',
  'link_not_aggregated',
  'link_already_linked',
  'link_type_mismatch',
  'link_currency_mismatch',
] as const;

function linkErrorKey(message: string): TranslationKey {
  const code = LINK_ERRORS.find((c) => message.includes(c));
  return code ? (`accountLink.error.${code}` as TranslationKey) : 'accountLink.failed';
}

async function runLink(manualId: string, bankId: string, dryRun: boolean): Promise<LinkSummary> {
  const { data, error } = await supabase.rpc('link_manual_account_to_powens', {
    p_manual_account_id: manualId,
    p_powens_account_id: bankId,
    p_dry_run: dryRun,
  });
  if (error) throw new Error(error.message);
  return data as LinkSummary;
}

/** 'YYYY-MM-DD' read as a calendar day, not as UTC midnight shifted into the local zone. */
function formatDay(day: string, language: string): string {
  const [y, m, d] = day.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB', {
    day: 'numeric',
    month: 'long',
  });
}

interface LinkBankAccountModalProps {
  /** The manual account being synchronised; null keeps the modal closed. */
  account: Account | null;
  accounts: Account[];
  /**
   * The bank connection whose accounts to offer — the one the user has just
   * connected. Null until the sync has recorded it.
   */
  connectionId: string | null;
  /**
   * The user is back from their bank. Until then the modal explains what is
   * about to happen and shows no list at all.
   */
  hasReturnedFromBank: boolean;
  /** That connection has not delivered its accounts yet. */
  isBankSyncing: boolean;
  isConnecting: boolean;
  onConnectBank: (account: Account) => void;
  onClose: () => void;
  onLinked: (summary: LinkSummary) => void;
}

export function LinkBankAccountModal({
  account,
  accounts,
  connectionId,
  hasReturnedFromBank,
  isBankSyncing,
  isConnecting,
  onConnectBank,
  onClose,
  onLinked,
}: LinkBankAccountModalProps) {
  const { t, language } = useTranslation();
  const { formatAmountInCurrency } = useCurrency();

  const [step, setStep] = useState<'review' | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [preview, setPreview] = useState<LinkSummary | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [errorKey, setErrorKey] = useState<TranslationKey | null>(null);

  const candidates = bankAccountsOfConnection(accounts, connectionId);
  const connectedBank = findBankByName(candidates[0]?.institution);

  // The account it used to be, or the only one that can be picked: either way
  // it is preselected, and a choice the user has made is never overwritten.
  const previous = account ? previouslySyncedCandidate(account, candidates, accounts) : undefined;
  const usable = account
    ? candidates.filter((c) => blockingReason(account, c, accounts) === null)
    : [];
  useEffect(() => {
    if (selectedId) return;
    if (previous) setSelectedId(previous.id);
    else if (usable.length === 1) setSelectedId(usable[0].id);
  }, [previous, usable, selectedId]);

  if (!account) return null;

  const currency = account.currency || 'EUR';
  const signed = (balance: number) =>
    account.type === 'liability' ? -Math.abs(balance) : Number(balance);
  const name = account.name;
  const current = step ?? (hasReturnedFromBank ? 'choose' : 'intro');

  const goToReview = async () => {
    if (!selectedId) return;
    setStep('review');
    setPreview(null);
    setErrorKey(null);
    setIsLoadingPreview(true);
    try {
      setPreview(await runLink(account.id, selectedId, true));
    } catch (err) {
      setErrorKey(linkErrorKey(err instanceof Error ? err.message : ''));
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const confirm = async () => {
    if (!selectedId) return;
    setIsLinking(true);
    setErrorKey(null);
    try {
      onLinked(await runLink(account.id, selectedId, false));
    } catch (err) {
      setErrorKey(linkErrorKey(err instanceof Error ? err.message : ''));
      setIsLinking(false);
    }
  };

  const since = preview ? formatDay(preview.transactions_from, language) : '';

  // French treats 0 and 1 as singular; English only 1.
  const counted = (key: TranslationKey, count: number) =>
    t((count === 1 || (language === 'fr' && count === 0) ? `${key}One` : key) as TranslationKey)
      .replace('{count}', String(count))
      .replace('{date}', since);

  return (
    <Dialog open onOpenChange={(open) => !open && !isLinking && onClose()}>
      <DialogContent className="max-h-[90vh] gap-6 overflow-y-auto border-border bg-card text-card-foreground sm:max-w-md">
        {current === 'intro' && (
          <>
            <DialogHeader>
              <DialogTitle>{t('accountLink.introTitle').replace('{name}', name)}</DialogTitle>
              <DialogDescription>{t('accountLink.introDesc')}</DialogDescription>
            </DialogHeader>

            <ol className="space-y-3 text-sm">
              <Consequence icon={ShieldCheck}>{t('accountLink.introStep1')}</Consequence>
              <Consequence icon={Landmark}>
                {t('accountLink.introStep2').replace('{name}', name)}
              </Consequence>
              <Consequence icon={History}>{t('accountLink.introStep3')}</Consequence>
            </ol>

            <DialogFooter className="gap-2 sm:justify-between sm:space-x-0">
              <Button variant="ghost" onClick={onClose}>
                {t('accountLink.cancel')}
              </Button>
              <Button disabled={isConnecting} onClick={() => onConnectBank(account)}>
                {isConnecting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isConnecting ? t('powens.connecting') : t('accountLink.chooseBank')}
              </Button>
            </DialogFooter>
          </>
        )}

        {current === 'choose' && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3">
                {/* Once, not per row: every account here comes from that bank. */}
                {connectedBank && (
                  <img
                    src={getBankLogoUrl(connectedBank)}
                    alt=""
                    className="h-8 w-8 shrink-0 rounded-md object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                <DialogTitle>{t('accountLink.chooseTitle')}</DialogTitle>
              </div>
              <DialogDescription>
                {t('accountLink.chooseDesc').replace('{name}', name)}
              </DialogDescription>
            </DialogHeader>

            {candidates.length === 0 ? (
              <div className="flex flex-col items-center px-6 py-4 text-center">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 dark:bg-brand-900/20">
                  {isBankSyncing ? (
                    <Loader2 className="h-6 w-6 animate-spin text-brand-700 dark:text-brand-400" />
                  ) : (
                    <Landmark className="h-6 w-6 text-brand-700 dark:text-brand-400" />
                  )}
                </div>
                <p className="text-base font-medium">
                  {isBankSyncing ? t('accountLink.syncing') : t('accountLink.noBankAccounts')}
                </p>
                <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                  {isBankSyncing
                    ? t('accountLink.syncingDesc')
                    : t('accountLink.noBankAccountsDesc')}
                </p>
              </div>
            ) : (
              <div
                role="radiogroup"
                aria-label={t('accountLink.chooseTitle')}
                className="divide-y divide-border overflow-hidden rounded-lg border border-border"
              >
                {candidates.map((candidate) => {
                  const blocked = blockingReason(account, candidate, accounts);
                  const selected = candidate.id === selectedId;
                  return (
                    <button
                      key={candidate.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      disabled={blocked !== null}
                      onClick={() => setSelectedId(candidate.id)}
                      className={cn(
                        'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                        blocked ? 'cursor-not-allowed opacity-60' : 'hover:bg-accent/50',
                        selected && 'bg-accent'
                      )}
                    >
                      <span
                        className={cn(
                          'flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                          selected ? 'border-primary bg-primary' : 'border-border'
                        )}
                      >
                        {selected && <Check className="h-3 w-3 text-primary-foreground" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{candidate.name}</span>
                        <span
                          className={cn(
                            'block truncate text-xs',
                            candidate.id === previous?.id && !blocked
                              ? 'font-medium text-brand-700 dark:text-brand-400'
                              : 'text-muted-foreground'
                          )}
                        >
                          {blocked
                            ? t(BLOCKED_KEY[blocked])
                            : candidate.id === previous?.id
                              ? t('accountLink.previousAccount')
                              : [
                                  candidate.institution,
                                  t(`accounts.subtype.${candidate.subtype}` as TranslationKey),
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                        </span>
                      </span>
                      <span className="shrink-0 text-sm font-medium tabular-nums">
                        {formatAmountInCurrency(
                          candidate.type === 'liability'
                            ? -Math.abs(candidate.balance)
                            : candidate.balance,
                          candidate.currency || currency
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            <DialogFooter className="gap-2 sm:justify-between sm:space-x-0">
              <Button
                variant="ghost"
                disabled={isConnecting}
                onClick={() => onConnectBank(account)}
              >
                {isConnecting ? t('powens.connecting') : t('accountLink.otherBank')}
              </Button>
              <Button disabled={!selectedId} onClick={goToReview}>
                {t('accountLink.continue')}
              </Button>
            </DialogFooter>
          </>
        )}

        {current === 'review' && (
          <>
            <DialogHeader>
              <DialogTitle>{t('accountLink.reviewTitle')}</DialogTitle>
              {preview && (
                <DialogDescription>
                  {name} ←{' '}
                  {[preview.bank_account_name, preview.institution].filter(Boolean).join(' · ')}
                </DialogDescription>
              )}
            </DialogHeader>

            {isLoadingPreview ? (
              <div className="space-y-4" aria-busy="true">
                <div className="h-4 w-16 animate-pulse rounded-md bg-muted" />
                <div className="h-8 w-56 animate-pulse rounded-md bg-muted" />
                <div className="space-y-3 border-t border-border pt-4">
                  <div className="h-4 w-full animate-pulse rounded-md bg-muted" />
                  <div className="h-4 w-4/5 animate-pulse rounded-md bg-muted" />
                  <div className="h-4 w-3/5 animate-pulse rounded-md bg-muted" />
                </div>
              </div>
            ) : preview ? (
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground">{t('accountLink.balance')}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 tabular-nums">
                    <span className="text-lg text-muted-foreground line-through decoration-1">
                      {formatAmountInCurrency(signed(preview.balance_before), currency)}
                    </span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    <span className="text-3xl font-semibold tracking-tight">
                      {formatAmountInCurrency(signed(preview.balance_after), currency)}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('accountLink.balanceHint')}
                  </p>
                </div>

                <ul className="space-y-3 border-t border-border pt-4 text-sm">
                  <Consequence icon={Landmark}>
                    {counted('accountLink.bankTransactions', preview.bank_transactions)}
                  </Consequence>
                  {preview.merged_duplicates > 0 && (
                    <Consequence icon={GitMerge}>
                      {counted('accountLink.merged', preview.merged_duplicates)}
                    </Consequence>
                  )}
                  {preview.recurring_paused > 0 && (
                    <Consequence icon={PauseCircle}>
                      {counted('accountLink.recurring', preview.recurring_paused)}
                    </Consequence>
                  )}
                  <Consequence icon={History}>
                    {t('accountLink.historyKept').replace('{date}', since)}
                  </Consequence>
                </ul>

                {preview.unmatched_manual > 0 && (
                  <div className="rounded-lg bg-amber-50 p-4 dark:bg-amber-900/20">
                    <div className="flex gap-3">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
                          {counted('accountLink.unmatchedTitle', preview.unmatched_manual)}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {counted('accountLink.unmatchedDesc', preview.unmatched_manual)}
                        </p>
                        <ul className="mt-3 space-y-2">
                          {preview.unmatched_manual_sample.slice(0, 5).map((entry) => (
                            <li key={entry.id} className="flex items-baseline gap-3 text-xs">
                              <span className="min-w-0 flex-1 truncate">
                                {entry.description || '—'}
                                <span className="text-muted-foreground">
                                  {' · '}
                                  {formatDay(entry.date, language)}
                                </span>
                              </span>
                              <span className="shrink-0 font-medium tabular-nums">
                                {formatAmountInCurrency(
                                  entry.type === 'income' ? entry.amount : -entry.amount,
                                  currency
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            {errorKey && <p className="text-xs text-destructive">{t(errorKey)}</p>}

            <DialogFooter className="gap-2 sm:justify-between sm:space-x-0">
              <Button
                variant="ghost"
                disabled={isLinking}
                onClick={() => {
                  setStep(null);
                  setErrorKey(null);
                }}
              >
                {t('accountLink.back')}
              </Button>
              <Button disabled={!preview || isLinking} onClick={confirm}>
                {isLinking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('accountLink.confirm')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Consequence({ icon: Icon, children }: { icon: typeof Landmark; children: ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <span>{children}</span>
    </li>
  );
}
